import { useState, useRef, useEffect } from 'react';
import { saveLead, markAsInteressado } from '@/services/leads-site';

interface Opcao {
  texto: string;
  pontos: number;
}

interface Pergunta {
  id: string;
  area: string;
  areaKey: string;
  icon: string;
  pergunta: string;
  opcoes: Opcao[];
  condicional?: {
    caminhoA: number[];
    proximasA: string[];
    proximasB: string[];
  };
}

interface Nivel {
  nivel: number;
  titulo: string;
  subtitulo: string;
  mensagem: string;
  problemas: string[];
  borderColor: string;
  bgCard: string;
  textColor: string;
  badgeBg: string;
  emoji: string;
  posicao: number;
}

interface AreaResult {
  area: string;
  areaKey: string;
  icon: string;
  pontos: number;
  maxPontos: number;
  cor: string;
  corBg: string;
  corText: string;
  diagnostico: string;
  recomendacao: string;
}

const perguntasMap: Record<string, Pergunta> = {
  p1: {
    id: 'p1', area: 'Controle Financeiro', areaKey: 'financeiro',
    icon: 'ri-money-dollar-circle-line',
    pergunta: 'Você possui controle claro do fluxo de caixa?',
    opcoes: [
      { texto: 'Não tenho controle', pontos: 0 },
      { texto: 'Tenho uma noção aproximada', pontos: 1 },
      { texto: 'Tenho controle mensal', pontos: 2 },
      { texto: 'Tenho controle semanal ou diário', pontos: 3 },
    ],
    condicional: {
      caminhoA: [0, 1],
      proximasA: ['p1b', 'p1c', 'p1d', 'p1e', 'p1f', 'p1g'],
      proximasB: ['p2'],
    },
  },
  p1b: {
    id: 'p1b', area: 'Controle Financeiro', areaKey: 'financeiro',
    icon: 'ri-money-dollar-circle-line',
    pergunta: 'Você sabe exatamente quanto entra e quanto sai de dinheiro no seu negócio por mês?',
    opcoes: [
      { texto: 'Não faço ideia', pontos: 0 },
      { texto: 'Tenho uma ideia aproximada', pontos: 1 },
      { texto: 'Sei o valor aproximado, mas não com precisão', pontos: 2 },
      { texto: 'Sei exatamente — com registro mensal', pontos: 3 },
    ],
  },
  p1c: {
    id: 'p1c', area: 'Controle Financeiro', areaKey: 'financeiro',
    icon: 'ri-money-dollar-circle-line',
    pergunta: 'Você consegue distinguir claramente seus custos fixos dos custos variáveis?',
    opcoes: [
      { texto: 'Não sei nem o que é isso', pontos: 0 },
      { texto: 'Tenho uma noção, mas sem clareza', pontos: 1 },
      { texto: 'Consigo separar parcialmente', pontos: 2 },
      { texto: 'Sim — e monitoro cada categoria', pontos: 3 },
    ],
  },
  p1d: {
    id: 'p1d', area: 'Controle Financeiro', areaKey: 'financeiro',
    icon: 'ri-money-dollar-circle-line',
    pergunta: 'Você sabe qual é a margem de lucro real de cada produto ou serviço que oferece?',
    opcoes: [
      { texto: 'Não calculo isso', pontos: 0 },
      { texto: 'Estimo de cabeça', pontos: 1 },
      { texto: 'Calculo para alguns produtos', pontos: 2 },
      { texto: 'Sei para todos e acompanho frequentemente', pontos: 3 },
    ],
  },
  p1e: {
    id: 'p1e', area: 'Controle Financeiro', areaKey: 'financeiro',
    icon: 'ri-money-dollar-circle-line',
    pergunta: 'Você mantém uma reserva financeira separada do capital de giro do negócio?',
    opcoes: [
      { texto: 'Não tenho reserva', pontos: 0 },
      { texto: 'Tenho pouco e de forma irregular', pontos: 1 },
      { texto: 'Tenho reserva, mas misturada com o capital', pontos: 2 },
      { texto: 'Tenho reserva dedicada e capital de giro separado', pontos: 3 },
    ],
  },
  p1f: {
    id: 'p1f', area: 'Controle Financeiro', areaKey: 'financeiro',
    icon: 'ri-money-dollar-circle-line',
    pergunta: 'Você faz um planejamento financeiro (orçamento) para o seu negócio?',
    opcoes: [
      { texto: 'Não faço planejamento', pontos: 0 },
      { texto: 'Faço de forma bem informal', pontos: 1 },
      { texto: 'Faço um planejamento anual', pontos: 2 },
      { texto: 'Faço planejamento mensal com revisões frequentes', pontos: 3 },
    ],
  },
  p1g: {
    id: 'p1g', area: 'Controle Financeiro', areaKey: 'financeiro',
    icon: 'ri-money-dollar-circle-line',
    pergunta: 'Nos últimos 6 meses, o lucro do negócio ficou dentro do esperado no seu planejamento?',
    opcoes: [
      { texto: 'Não tenho ideia — nunca controlo isso', pontos: 0 },
      { texto: 'Sempre fica abaixo do esperado', pontos: 1 },
      { texto: 'Fica próximo, mas com variações grandes', pontos: 2 },
      { texto: 'Sempre atinge ou supera o esperado', pontos: 3 },
    ],
  },
  p1h: {
    id: 'p1h', area: 'Controle Financeiro', areaKey: 'financeiro',
    icon: 'ri-money-dollar-circle-line',
    pergunta: 'Você sabe quanto tem para receber de clientes e quanto deve pagar de fornecedores a cada mês?',
    opcoes: [
      { texto: 'Não faço ideia — quem me deve', pontos: 0 },
      { texto: 'Tenho controle informal, mas sem certeza dos valores', pontos: 1 },
      { texto: 'Sei os valores aproximados, mas sem precisão', pontos: 2 },
      { texto: 'Sei exatamente — com controle de contas a pagar e receber', pontos: 3 },
    ],
  },
  p1i: {
    id: 'p1i', area: 'Controle Financeiro', areaKey: 'financeiro',
    icon: 'ri-money-dollar-circle-line',
    pergunta: 'Você sabe qual é o custo fixo mensal do seu negócio (aluguel, salários, água, luz, etc.)?',
    opcoes: [
      { texto: 'Não sei — nunca calculei', pontos: 0 },
      { texto: 'Tenho uma noção bem vaga', pontos: 1 },
      { texto: 'Sei o valor aproximado, mas pode variar', pontos: 2 },
      { texto: 'Sei exatamente — com planilha ou sistema atualizado', pontos: 3 },
    ],
  },
  p1j: {
    id: 'p1j', area: 'Controle Financeiro', areaKey: 'financeiro',
    icon: 'ri-money-dollar-circle-line',
    pergunta: 'Você consegue saber, a qualquer momento, se o mês vai fechar no lucro ou no vermelho?',
    opcoes: [
      { texto: 'Não — só descubro quando olho a conta no fim do mês', pontos: 0 },
      { texto: 'Às vezes dá para perceber, mas sem certeza', pontos: 1 },
      { texto: 'Tenho uma boa ideia de como vai fechar', pontos: 2 },
      { texto: 'Sim — acompanho em tempo real com indicadores', pontos: 3 },
    ],
  },
  p2: {
    id: 'p2', area: 'Vendas', areaKey: 'vendas',
    icon: 'ri-hand-coin-line',
    pergunta: 'Seu negócio possui um processo estruturado de vendas?',
    opcoes: [
      { texto: 'Não existe processo', pontos: 0 },
      { texto: 'Vendas acontecem de forma informal', pontos: 1 },
      { texto: 'Temos algum controle', pontos: 2 },
      { texto: 'Existe funil e métricas claras', pontos: 3 },
    ],
    condicional: {
      caminhoA: [0, 1],
      proximasA: ['p2b'],
      proximasB: ['p2c'],
    },
  },
  p2b: {
    id: 'p2b', area: 'Vendas', areaKey: 'vendas',
    icon: 'ri-hand-coin-line',
    pergunta: 'Como são definidos os preços dos seus produtos ou serviços hoje?',
    opcoes: [
      { texto: 'No feeling — vou ajustando no dia a dia', pontos: 0 },
      { texto: 'Com base nos custos + uma margem', pontos: 1 },
      { texto: 'Com alguma análise de mercado e competição', pontos: 2 },
      { texto: 'Com análise de margem, mercado, competitor e dados', pontos: 3 },
    ],
  },
  p2c: {
    id: 'p2c', area: 'Vendas', areaKey: 'vendas',
    icon: 'ri-hand-coin-line',
    pergunta: 'Você sabe exatamente quantos clientes novos precisa por mês para bater sua meta?',
    opcoes: [
      { texto: 'Não sei — nunca calculei isso', pontos: 0 },
      { texto: 'Tenho uma meta, mas não sei o número exato', pontos: 1 },
      { texto: 'Sei o número, mas não acompanho todo mês', pontos: 2 },
      { texto: 'Sei e acompanho com precisão mensalmente', pontos: 3 },
    ],
  },
  p3: {
    id: 'p3', area: 'Gestão', areaKey: 'gestao',
    icon: 'ri-settings-3-line',
    pergunta: 'As atividades da empresa estão organizadas por processos?',
    opcoes: [
      { texto: 'Não', pontos: 0 },
      { texto: 'Parcialmente', pontos: 1 },
      { texto: 'Sim, mas sem documentação', pontos: 2 },
      { texto: 'Sim, com processos definidos', pontos: 3 },
    ],
    condicional: {
      caminhoA: [0, 1],
      proximasA: ['p3b', 'p3c'],
      proximasB: ['p3d'],
    },
  },
  p3b: {
    id: 'p3b', area: 'Gestão', areaKey: 'gestao',
    icon: 'ri-settings-3-line',
    pergunta: 'Quando um cliente faz uma reclamação, você tem um processo definido para resolver?',
    opcoes: [
      { texto: 'Não — cada situação é resolvida do jeito que dá', pontos: 0 },
      { texto: 'Às vezes resolvo, mas sem seguir um padrão', pontos: 1 },
      { texto: 'Tenho uma ideia de como resolver, mas não é formal', pontos: 2 },
      { texto: 'Sim — existe um fluxo definido de atendimento', pontos: 3 },
    ],
  },
  p3c: {
    id: 'p3c', area: 'Gestão', areaKey: 'gestao',
    icon: 'ri-settings-3-line',
    pergunta: 'Você consegue listar as 3 principais atividades que mais tomam tempo na sua empresa?',
    opcoes: [
      { texto: 'Não sei — sempre tem muita coisa ao mesmo tempo', pontos: 0 },
      { texto: 'Tenho uma ideia, mas nunca parei para mapear', pontos: 1 },
      { texto: 'Sei quais são, mas não sei se são as mais críticas', pontos: 2 },
      { texto: 'Sim — mapeei e otimizo o tempo com isso', pontos: 3 },
    ],
  },
  p3d: {
    id: 'p3d', area: 'Gestão', areaKey: 'gestao',
    icon: 'ri-settings-3-line',
    pergunta: 'Você já documentou os processos principais da empresa (manual, POP, etc.)?',
    opcoes: [
      { texto: 'Nunca — nem sei por onde começar', pontos: 0 },
      { texto: 'Tenho algumas anotações mas nada organizadoo', pontos: 1 },
      { texto: 'Tenho alguns documentos, mas estão incompletos', pontos: 2 },
      { texto: 'Sim — tenho manuais e procedimentos atualizados', pontos: 3 },
    ],
  },
  p4: {
    id: 'p4', area: 'Indicadores', areaKey: 'indicadores',
    icon: 'ri-bar-chart-2-line',
    pergunta: 'Você acompanha indicadores do negócio?',
    opcoes: [
      { texto: 'Não acompanho', pontos: 0 },
      { texto: 'Apenas faturamento', pontos: 1 },
      { texto: 'Alguns indicadores', pontos: 2 },
      { texto: 'Painel completo de indicadores', pontos: 3 },
    ],
  },
  p5: {
    id: 'p5', area: 'Dependência do Dono', areaKey: 'dependencia',
    icon: 'ri-user-star-line',
    pergunta: 'Se você ficar uma semana fora, o negócio funciona?',
    opcoes: [
      { texto: 'Não funciona', pontos: 0 },
      { texto: 'Funciona com dificuldade', pontos: 1 },
      { texto: 'Funciona parcialmente', pontos: 2 },
      { texto: 'Funciona normalmente', pontos: 3 },
    ],
  },
  p6: {
    id: 'p6', area: 'Tecnologia', areaKey: 'tecnologia',
    icon: 'ri-robot-2-line',
    pergunta: 'Você utiliza ferramentas digitais ou automação?',
    opcoes: [
      { texto: 'Não utilizo', pontos: 0 },
      { texto: 'Uso ferramentas básicas', pontos: 1 },
      { texto: 'Uso algumas automações', pontos: 2 },
      { texto: 'Uso ferramentas e IA', pontos: 3 },
    ],
  },
  p7: {
    id: 'p7', area: 'Time Comercial', areaKey: 'time_comercial',
    icon: 'ri-team-line',
    pergunta: 'Hoje, sua empresa conta com um time comercial?',
    opcoes: [
      { texto: 'Sim, tenho um time comercial estruturado', pontos: 3 },
      { texto: 'Tenho apenas uma ou duas pessoas atuando nas vendas', pontos: 2 },
      { texto: 'Não, hoje sou eu quem faz as vendas', pontos: 1 },
      { texto: 'Não tenho time e as vendas acontecem sem estrutura definida', pontos: 0 },
    ],
    condicional: {
      caminhoA: [0, 1],
      proximasA: ['p8', 'p9'],
      proximasB: ['p7b'],
    },
  },
  p7b: {
    id: 'p7b', area: 'Time Comercial', areaKey: 'time_comercial',
    icon: 'ri-user-add-line',
    pergunta: 'Você acredita que sua empresa precisa montar um time comercial para continuar crescendo?',
    opcoes: [
      { texto: 'Sim, com urgência', pontos: 3 },
      { texto: 'Sim, mas ainda não sei como estruturar isso', pontos: 2 },
      { texto: 'Talvez, mas ainda estou tentando dar conta sozinho(a)', pontos: 1 },
      { texto: 'Não, por enquanto não vejo essa necessidade', pontos: 0 },
    ],
  },
  p8: {
    id: 'p8', area: 'Rotina Comercial', areaKey: 'time_comercial',
    icon: 'ri-calendar-check-line',
    pergunta: 'O time comercial sabe exatamente o que precisa fazer todos os dias?',
    opcoes: [
      { texto: 'Sim, existe rotina comercial com metas, atividades e acompanhamento', pontos: 3 },
      { texto: 'Em parte, mas ainda falta mais clareza e disciplina na execução', pontos: 2 },
      { texto: 'O time até sabe o objetivo, mas não existe uma rotina bem definida', pontos: 1 },
      { texto: 'Não, cada pessoa atua como acha melhor', pontos: 0 },
    ],
  },
  p9: {
    id: 'p9', area: 'Autonomia Comercial', areaKey: 'time_comercial',
    icon: 'ri-shield-check-line',
    pergunta: 'Se você precisasse se afastar por 15 dias, o comercial continuaria performando?',
    opcoes: [
      { texto: 'Sim, porque existe processo, gestão e acompanhamento', pontos: 3 },
      { texto: 'Continuaria parcialmente, com alguma queda', pontos: 2 },
      { texto: 'Teria bastante instabilidade', pontos: 1 },
      { texto: 'Provavelmente travaria', pontos: 0 },
    ],
  },
  p6b: {
    id: 'p6b', area: 'Tecnologia', areaKey: 'tecnologia',
    icon: 'ri-sparkling-2-line',
    pergunta: 'Você já utiliza Inteligência Artificial no seu negócio? Se sim, quais?',
    opcoes: [
      { texto: 'Não utilizo IA ainda', pontos: 0 },
      { texto: 'Uso ChatGPT ou similares para tarefas pontuais', pontos: 1 },
      { texto: 'Uso IA para conteúdo, atendimento ou análise de dados', pontos: 2 },
      { texto: 'Tenho fluxos de IA integrados à operação (automações, agentes, CRM)', pontos: 3 },
    ],
  },
  p6c: {
    id: 'p6c', area: 'Tecnologia', areaKey: 'tecnologia',
    icon: 'ri-robot-2-line',
    pergunta: 'Quais ferramentas digitais você usa hoje na operação do seu negócio?',
    opcoes: [
      { texto: 'Nenhuma — faço tudo no papel ou intuição', pontos: 0 },
      { texto: 'WhatsApp e planilhas simples', pontos: 1 },
      { texto: 'WhatsApp, planilhas e algumas ferramentas avulsas', pontos: 2 },
      { texto: 'Sistema integrado com CRM, automações e múltiplas ferramentas', pontos: 3 },
    ],
  },
  p6d: {
    id: 'p6d', area: 'Tecnologia', areaKey: 'tecnologia',
    icon: 'ri-computer-line',
    pergunta: 'Seus dados de vendas, financeiro e clientes estão todos organizados em um lugar só?',
    opcoes: [
      { texto: 'Não — estão espalhados em vários lugares', pontos: 0 },
      { texto: 'Parcialmente — tenho algumas coisas organizadas', pontos: 1 },
      { texto: 'Bastante coisa está organizada, mas não é 100%', pontos: 2 },
      { texto: 'Sim — tengo um sistema que centraliza tudo', pontos: 3 },
    ],
  },
  p10: {
    id: 'p10', area: 'Maturidade Jurídica', areaKey: 'juridico',
    icon: 'ri-scales-3-line',
    pergunta: 'Hoje, como funciona a criação de contratos e documentos na sua empresa?',
    opcoes: [
      { texto: 'É tudo feito no improviso, quando surge a necessidade', pontos: 0 },
      { texto: 'Temos alguns modelos, mas sem padrão claro', pontos: 1 },
      { texto: 'Já existe certa organização, mas ainda depende muito da equipe', pontos: 2 },
      { texto: 'Temos documentos padronizados, organizados e de fácil acesso', pontos: 3 },
    ],
  },
  p11: {
    id: 'p11', area: 'Maturidade Jurídica', areaKey: 'juridico',
    icon: 'ri-file-shield-2-line',
    pergunta: 'Qual é o nível de controle da sua empresa sobre contratos, documentos e registros importantes?',
    opcoes: [
      { texto: 'Baixo, os documentos ficam espalhados e sem centralização', pontos: 0 },
      { texto: 'Parcial, conseguimos localizar algumas coisas, mas com dificuldade', pontos: 1 },
      { texto: 'Bom, existe organização, mas ainda com falhas', pontos: 2 },
      { texto: 'Alto, tudo está centralizado, controlado e acessível', pontos: 3 },
    ],
  },
  p12: {
    id: 'p12', area: 'Maturidade Jurídica', areaKey: 'juridico',
    icon: 'ri-shield-keyhole-line',
    pergunta: 'Se hoje a empresa tiver um problema com cliente, fornecedor, sócio ou colaborador, quão preparada ela está juridicamente?',
    opcoes: [
      { texto: 'Pouco preparada, provavelmente teria dificuldade para se proteger', pontos: 0 },
      { texto: 'Razoavelmente preparada, mas com várias vulnerabilidades', pontos: 1 },
      { texto: 'Bem preparada, embora ainda existam alguns pontos de atenção', pontos: 2 },
      { texto: 'Muito preparada, com documentos e processos que dão segurança', pontos: 3 },
    ],
  },
};

const sequenciaBase = ['p1', 'p2', 'p3', 'p4', 'p5', 'p6', 'p6b', 'p6c', 'p6d', 'p7', 'p10', 'p11', 'p12'];

// Configuração de cada área para o resultado detalhado
const areaConfig: Record<string, {
  label: string; icon: string; cor: string; corBg: string; corText: string;
  diagnosticos: string[]; recomendacoes: string[];
}> = {
  financeiro: {
    label: 'Controle Financeiro', icon: 'ri-money-dollar-circle-line',
    cor: 'bg-emerald-500', corBg: 'bg-emerald-50', corText: 'text-emerald-700',
    diagnosticos: [
      'Sem controle financeiro — alto risco de crise de caixa',
      'Controle informal — decisões baseadas em estimativas',
      'Controle básico — falta visibilidade para crescer',
      'Controle sólido — base para escalar com segurança',
    ],
    recomendacoes: [
      'Implante urgentemente um fluxo de caixa simples e separe finanças pessoais das empresariais.',
      'Estruture um DRE simplificado e comece a registrar todas as entradas e saídas.',
      'Avance para indicadores como margem de contribuição e ponto de equilíbrio.',
      'Otimize com dashboard financeiro e automatize relatórios para decisões mais rápidas.',
    ],
  },
  vendas: {
    label: 'Processo de Vendas', icon: 'ri-hand-coin-line',
    cor: 'bg-orange-500', corBg: 'bg-orange-50', corText: 'text-orange-700',
    diagnosticos: [
      'Vendas no improviso — receita imprevisível e instável',
      'Processo informal — depende do feeling de cada vendedor',
      'Processo parcial — falta padronização e métricas',
      'Processo estruturado — vendas previsíveis e escaláveis',
    ],
    recomendacoes: [
      'Crie um processo básico de vendas: prospecção, abordagem, proposta e fechamento.',
      'Documente o processo atual e defina etapas claras para cada venda.',
      'Implante um CRM simples e comece a medir taxa de conversão.',
      'Refine scripts, automatize follow-ups e expanda o time com processo replicável.',
    ],
  },
  gestao: {
    label: 'Gestão e Processos', icon: 'ri-settings-3-line',
    cor: 'bg-sky-500', corBg: 'bg-sky-50', corText: 'text-sky-700',
    diagnosticos: [
      'Sem processos — operação caótica e dependente do dono',
      'Processos informais — conhecimento concentrado em pessoas',
      'Processos parciais — falta documentação e padronização',
      'Processos definidos — operação previsível e delegável',
    ],
    recomendacoes: [
      'Mapeie as 3 atividades mais críticas e crie um passo a passo simples para cada.',
      'Documente os processos existentes e defina responsáveis claros.',
      'Crie um manual operacional e implante rotinas de acompanhamento.',
      'Automatize processos repetitivos e foque em melhoria contínua.',
    ],
  },
  indicadores: {
    label: 'Indicadores', icon: 'ri-bar-chart-2-line',
    cor: 'bg-violet-500', corBg: 'bg-violet-50', corText: 'text-violet-700',
    diagnosticos: [
      'Sem indicadores — decisões no escuro',
      'Apenas faturamento — visão limitada do negócio',
      'Indicadores parciais — falta visão integrada',
      'Painel completo — gestão orientada por dados',
    ],
    recomendacoes: [
      'Comece com 3 indicadores: faturamento, ticket médio e número de clientes.',
      'Adicione indicadores de custo, margem e inadimplência.',
      'Crie um painel semanal com os principais KPIs do negócio.',
      'Implante BI ou dashboard automatizado para decisões em tempo real.',
    ],
  },
  dependencia: {
    label: 'Dependência do Dono', icon: 'ri-user-star-line',
    cor: 'bg-red-500', corBg: 'bg-red-50', corText: 'text-red-700',
    diagnosticos: [
      'Total dependência — negócio para sem o dono',
      'Alta dependência — operação instável sem o dono',
      'Dependência parcial — alguns processos funcionam sozinhos',
      'Baixa dependência — negócio funciona de forma autônoma',
    ],
    recomendacoes: [
      'Identifique as 5 tarefas que só você faz e comece a documentar e delegar uma por vez.',
      'Treine um colaborador para assumir responsabilidades operacionais.',
      'Crie um manual de operações e implante reuniões de acompanhamento.',
      'Foque em liderança estratégica e deixe a operação rodar sem sua presença diária.',
    ],
  },
  tecnologia: {
    label: 'Tecnologia e IA', icon: 'ri-robot-2-line',
    cor: 'bg-rose-500', corBg: 'bg-rose-50', corText: 'text-rose-700',
    diagnosticos: [
      'Sem tecnologia — operação manual e lenta',
      'Tecnologia básica — potencial desperdiçado',
      'Automações parciais — ganhos limitados',
      'Tecnologia avançada — vantagem competitiva real',
    ],
    recomendacoes: [
      'Comece com ferramentas gratuitas: Google Workspace, Trello e WhatsApp Business.',
      'Implante um CRM e automatize o envio de mensagens e follow-ups.',
      'Use IA para criar conteúdo, responder clientes e analisar dados.',
      'Integre sistemas e crie fluxos de automação para ganhar escala sem aumentar equipe.',
    ],
  },
  time_comercial: {
    label: 'Time Comercial', icon: 'ri-team-line',
    cor: 'bg-amber-500', corBg: 'bg-amber-50', corText: 'text-amber-700',
    diagnosticos: [
      'Sem estrutura comercial — vendas dependem do acaso',
      'Estrutura inicial — falta processo e previsibilidade',
      'Estrutura parcial — time existe mas sem rotina clara',
      'Estrutura sólida — time autônomo e orientado a resultados',
    ],
    recomendacoes: [
      'Defina metas claras e crie um processo básico de prospecção e atendimento.',
      'Estruture uma rotina comercial diária com metas, atividades e acompanhamento.',
      'Implante reuniões de pipeline e acompanhe conversão por etapa do funil.',
      'Automatize relatórios e foque em recrutamento e treinamento para expansão.',
    ],
  },
  juridico: {
    label: 'Maturidade Jurídica', icon: 'ri-scales-3-line',
    cor: 'bg-indigo-600', corBg: 'bg-indigo-50', corText: 'text-indigo-700',
    diagnosticos: [
      'Exposição jurídica crítica — empresa vulnerável a conflitos',
      'Baixa proteção jurídica — documentos improvisados e dispersos',
      'Proteção parcial — organização existe mas com lacunas',
      'Proteção jurídica sólida — empresa blindada e organizada',
    ],
    recomendacoes: [
      'Formalize urgentemente contratos com clientes, fornecedores e colaboradores.',
      'Centralize todos os documentos em uma pasta digital organizada por categoria.',
      'Padronize contratos e crie uma biblioteca de documentos de fácil acesso.',
      'Implante uma plataforma jurídica empresarial e revise contratos periodicamente.',
    ],
  },
};

const niveis: Nivel[] = [
  {
    nivel: 1, titulo: 'Negócio em Risco', subtitulo: 'Alta desorganização e dependência total do dono.',
    mensagem: 'Seu negócio possui gargalos críticos que limitam crescimento.',
    problemas: [
      'Fluxo de caixa descontrolado gerando crises constantes',
      'Vendas imprevisíveis sem processo definido',
      'Exposição jurídica elevada — sem contratos ou documentos organizados',
      'Tudo depende de você — sem delegação real',
    ],
    borderColor: 'border-red-400', bgCard: 'bg-red-50', textColor: 'text-red-700',
    badgeBg: 'bg-red-100', emoji: '🚨', posicao: 12,
  },
  {
    nivel: 2, titulo: 'Negócio Desorganizado', subtitulo: 'Algumas áreas funcionam, mas sem estrutura clara.',
    mensagem: 'Você tem potencial de crescimento, mas falta estrutura.',
    problemas: [
      'Processos existem na cabeça, não no papel',
      'Equipe sem direção clara e retrabalho constante',
      'Contratos e documentos improvisados — risco jurídico latente',
      'Decisões tomadas no achismo sem dados concretos',
    ],
    borderColor: 'border-orange-400', bgCard: 'bg-orange-50', textColor: 'text-orange-700',
    badgeBg: 'bg-orange-100', emoji: '⚠️', posicao: 37,
  },
  {
    nivel: 3, titulo: 'Negócio Estruturando', subtitulo: 'Já existe controle em algumas áreas.',
    mensagem: 'Seu negócio está no caminho certo, mas ainda existem gargalos.',
    problemas: [
      'Falta padronização para escalar sem perder qualidade',
      'Indicadores parciais dificultam decisões estratégicas',
      'Proteção jurídica parcial — ainda existem vulnerabilidades',
      'Automação ainda não está sendo aproveitada ao máximo',
    ],
    borderColor: 'border-amber-400', bgCard: 'bg-amber-50', textColor: 'text-amber-700',
    badgeBg: 'bg-amber-100', emoji: '📈', posicao: 62,
  },
  {
    nivel: 4, titulo: 'Pronto para Escalar', subtitulo: 'Estrutura avançada com base sólida.',
    mensagem: 'Seu negócio possui boa base para crescimento acelerado.',
    problemas: [
      'Otimizar processos para suportar crescimento acelerado',
      'Implementar IA e automação para ganhar vantagem competitiva',
      'Blindagem jurídica completa para expansão segura',
      'Estruturar liderança para expansão sem depender do dono',
    ],
    borderColor: 'border-emerald-400', bgCard: 'bg-emerald-50', textColor: 'text-emerald-700',
    badgeBg: 'bg-emerald-100', emoji: '🚀', posicao: 88,
  },
];

function getNivel(pontuacao: number, pontuacaoMaxima: number): Nivel {
  const pct = pontuacaoMaxima > 0 ? pontuacao / pontuacaoMaxima : 0;
  if (pct <= 0.25) return niveis[0];
  if (pct <= 0.5) return niveis[1];
  if (pct <= 0.75) return niveis[2];
  return niveis[3];
}

interface RespostaRegistrada {
  perguntaId: string;
  areaKey: string;
  pontos: number;
  opcaoIndex: number;
  textoOpcao: string;
}

function calcularResultadosPorArea(respostas: RespostaRegistrada[], sequencia: string[]): AreaResult[] {
  // Agrupa pontos por areaKey
  const porArea: Record<string, { pontos: number; count: number }> = {};
  respostas.forEach((r) => {
    if (!porArea[r.areaKey]) porArea[r.areaKey] = { pontos: 0, count: 0 };
    porArea[r.areaKey].pontos += r.pontos;
    porArea[r.areaKey].count += 1;
  });

  // Ordem de exibição
  const ordem = ['financeiro', 'vendas', 'gestao', 'indicadores', 'dependencia', 'tecnologia', 'time_comercial', 'juridico'];

  return ordem
    .filter((key) => porArea[key])
    .map((key) => {
      const cfg = areaConfig[key];
      const { pontos, count } = porArea[key];
      const maxPontos = count * 3;
      const pct = maxPontos > 0 ? pontos / maxPontos : 0;
      const nivelIdx = pct <= 0.25 ? 0 : pct <= 0.5 ? 1 : pct <= 0.75 ? 2 : 3;
      return {
        area: cfg.label,
        areaKey: key,
        icon: cfg.icon,
        pontos,
        maxPontos,
        cor: cfg.cor,
        corBg: cfg.corBg,
        corText: cfg.corText,
        diagnostico: cfg.diagnosticos[nivelIdx],
        recomendacao: cfg.recomendacoes[nivelIdx],
      };
    });
}

function GaugeMeter({ posicao, size = 280 }: { posicao: number; size?: number }) {
  const cx = 140; const cy = 130; const r = 100;
  const angle = -180 + (posicao / 100) * 180;
  const rad = (angle * Math.PI) / 180;
  const px = cx + r * Math.cos(rad);
  const py = cy + r * Math.sin(rad);
  return (
    <div style={{ width: size, height: size * 0.65 }} className="relative">
      <svg viewBox="0 0 280 170" className="w-full h-full overflow-visible">
        <defs>
          <linearGradient id="gaugeGradFull" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ef4444" />
            <stop offset="45%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#10b981" />
          </linearGradient>
        </defs>
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="#ffffff15" strokeWidth="20" strokeLinecap="round" />
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="url(#gaugeGradFull)" strokeWidth="20" strokeLinecap="round" />
        <line x1={cx} y1={cy} x2={px} y2={py} stroke="#f59e0b" strokeWidth="4" strokeLinecap="round" />
        <circle cx={cx} cy={cy} r="10" fill="#f59e0b" />
        <circle cx={cx} cy={cy} r="5" fill="#111827" />
        <text x="18" y="165" fill="#f87171" fontSize="11" fontWeight="700" textAnchor="middle">Em Risco</text>
        <text x="262" y="165" fill="#34d399" fontSize="11" fontWeight="700" textAnchor="middle">Escalável</text>
        <text x="140" y="165" fill="#fbbf24" fontSize="11" fontWeight="600" textAnchor="middle">Estruturando</text>
      </svg>
    </div>
  );
}

function GaugeMeterDecorative() {
  return (
    <div className="w-64 mx-auto relative" style={{ height: 130 }}>
      <svg viewBox="0 0 280 170" className="w-full h-full overflow-visible">
        <defs>
          <linearGradient id="gaugeGradDeco" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ef4444" />
            <stop offset="45%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#10b981" />
          </linearGradient>
        </defs>
        <path d="M 40 130 A 100 100 0 0 1 240 130" fill="none" stroke="#ffffff10" strokeWidth="20" strokeLinecap="round" />
        <path d="M 40 130 A 100 100 0 0 1 240 130" fill="none" stroke="url(#gaugeGradDeco)" strokeWidth="20" strokeLinecap="round" />
        <line x1="140" y1="130" x2="140" y2="42" stroke="#f59e0b" strokeWidth="4" strokeLinecap="round" />
        <circle cx="140" cy="130" r="10" fill="#f59e0b" />
        <circle cx="140" cy="130" r="5" fill="#111827" />
        <text x="18" y="162" fill="#f87171" fontSize="11" fontWeight="700" textAnchor="middle">Em Risco</text>
        <text x="262" y="162" fill="#34d399" fontSize="11" fontWeight="700" textAnchor="middle">Escalável</text>
      </svg>
    </div>
  );
}

function BarraArea({ pontos, maxPontos, cor }: { pontos: number; maxPontos: number; cor: string }) {
  const pct = maxPontos > 0 ? Math.round((pontos / maxPontos) * 100) : 0;
  return (
    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
      <div className={`h-full ${cor} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function QuizSection() {
  const [iniciado, setIniciado] = useState(false);
  const [quizFullscreen, setQuizFullscreen] = useState(false);
  const [sequencia, setSequencia] = useState<string[]>(sequenciaBase);
  const [perguntaAtualIdx, setPerguntaAtualIdx] = useState(0);
  const [respostas, setRespostas] = useState<RespostaRegistrada[]>([]);
  const [opcaoSelecionada, setOpcaoSelecionada] = useState<number | null>(null);
  const [mostrarResultado, setMostrarResultado] = useState(false);
  const [animando, setAnimando] = useState(false);
  const [formEnviado, setFormEnviado] = useState(false);
  const [formEnviando, setFormEnviando] = useState(false);
  const [formErro, setFormErro] = useState('');
  const [abaResultado, setAbaResultado] = useState<'geral' | 'areas'>('geral');
  const [programaInteresse, setProgramaInteresse] = useState(false);
  const [leadEmail, setLeadEmail] = useState('');
  const [resultadoDesbloqueado, setResultadoDesbloqueado] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // Auto-scroll para o formulário quando o resultado aparece
  useEffect(() => {
    if (mostrarResultado && !resultadoDesbloqueado) {
      setTimeout(() => {
        document.getElementById('form-quiz-resultado')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [mostrarResultado, resultadoDesbloqueado]);

  const pontuacaoTotal = respostas.reduce((acc, r) => acc + r.pontos, 0);
  // Pontuação máxima = perguntas que realmente foram feitas × 3 (cada resposta max 3 pts)
  const pontuacaoMaxima = respostas.length * 3;
  const nivelAtual = getNivel(pontuacaoTotal, pontuacaoMaxima);
  const resultadosPorArea = calcularResultadosPorArea(respostas, sequencia);

  const handleIniciar = () => {
    setIniciado(true);
    setQuizFullscreen(true);
    setSequencia(sequenciaBase);
    setPerguntaAtualIdx(0);
    setRespostas([]);
    setOpcaoSelecionada(null);
    setMostrarResultado(false);
    setAbaResultado('geral');
  };

  const handleVoltar = () => {
    if (perguntaAtualIdx === 0) return;
    // Remove a última resposta
    const novasRespostas = respostas.slice(0, -1);
    setRespostas(novasRespostas);
    // Se a pergunta anterior tinha condicional, precisamos reconstruir a sequência sem as condicionais inseridas
    // Voltamos ao índice anterior e removemos perguntas condicionais que foram inseridas após ela
    const idxAnterior = perguntaAtualIdx - 1;
    const perguntaAnteriorId = sequencia[idxAnterior];
    const perguntaAnterior = perguntasMap[perguntaAnteriorId];
    if (perguntaAnterior?.condicional) {
      // Remove as perguntas condicionais que foram inseridas após a pergunta anterior
      const todasCondicionais = [
        ...perguntaAnterior.condicional.proximasA,
        ...perguntaAnterior.condicional.proximasB,
      ];
      const sequenciaSemCondicionais = [
        ...sequencia.slice(0, idxAnterior + 1),
        ...sequencia.slice(idxAnterior + 1).filter((id) => !todasCondicionais.includes(id)),
      ];
      setSequencia(sequenciaSemCondicionais);
    }
    setPerguntaAtualIdx(idxAnterior);
    setOpcaoSelecionada(null);
  };

  const handleSelecionar = (pontos: number, opcaoIndex: number) => {
    setOpcaoSelecionada(opcaoIndex);
    setTimeout(() => {
      const perguntaId = sequencia[perguntaAtualIdx];
      const pergunta = perguntasMap[perguntaId];
      const novasRespostas: RespostaRegistrada[] = [
        ...respostas,
        {
          perguntaId,
          areaKey: pergunta.areaKey,
          pontos,
          opcaoIndex,
          textoOpcao: pergunta.opcoes[opcaoIndex].texto,
        },
      ];
      setRespostas(novasRespostas);

      let novaSequencia = [...sequencia];
      if (pergunta.condicional) {
        const { caminhoA, proximasA, proximasB } = pergunta.condicional;
        const isA = caminhoA.includes(opcaoIndex);
        const proximas = isA ? proximasA : proximasB;
        novaSequencia = [
          ...sequencia.slice(0, perguntaAtualIdx + 1),
          ...proximas,
          ...sequencia.slice(perguntaAtualIdx + 1),
        ];
        setSequencia(novaSequencia);
      }

      setAnimando(true);
      setTimeout(() => {
        if (perguntaAtualIdx + 1 >= novaSequencia.length) {
          setMostrarResultado(true);
        } else {
          setPerguntaAtualIdx((prev) => prev + 1);
          setOpcaoSelecionada(null);
        }
        setAnimando(false);
      }, 300);
    }, 400);
  };

  const handleReiniciar = () => {
    setIniciado(false);
    setQuizFullscreen(false);
    setSequencia(sequenciaBase);
    setPerguntaAtualIdx(0);
    setRespostas([]);
    setOpcaoSelecionada(null);
    setMostrarResultado(false);
    setAnimando(false);
    setFormEnviado(false);
    setFormEnviando(false);
    setFormErro('');
    setAbaResultado('geral');
    setProgramaInteresse(false);
    setResultadoDesbloqueado(false);
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormErro('');
    const form = e.currentTarget as HTMLFormElement;
    const isQuizForm = form.id === 'form-quiz-resultado';
    if (!form) return;
    const data = new URLSearchParams();

    // Dados do lead
    const inputs = form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea, select');
    inputs.forEach((el) => { if (el.name) data.append(el.name, el.value); });

    // Resultado geral
    data.append('nivel_titulo', nivelAtual.titulo);
    data.append('nivel_numero', String(nivelAtual.nivel));
    data.append('pontuacao_total', String(pontuacaoTotal));
    data.append('pontuacao_maxima', String(pontuacaoMaxima));
    data.append('percentual_geral', `${Math.round((pontuacaoTotal / pontuacaoMaxima) * 100)}%`);
    data.append('subtitulo_nivel', nivelAtual.subtitulo);
    data.append('mensagem_diagnostico', nivelAtual.mensagem);

    // Resultado por área
    resultadosPorArea.forEach((ar) => {
      const pct = Math.round((ar.pontos / ar.maxPontos) * 100);
      data.append(`area_${ar.areaKey}_pontos`, `${ar.pontos}/${ar.maxPontos}`);
      data.append(`area_${ar.areaKey}_percentual`, `${pct}%`);
      data.append(`area_${ar.areaKey}_diagnostico`, ar.diagnostico);
      data.append(`area_${ar.areaKey}_recomendacao`, ar.recomendacao);
    });

    // Respostas individuais
    respostas.forEach((r, i) => {
      const p = perguntasMap[r.perguntaId];
      data.append(`resposta_${i + 1}_pergunta`, p?.pergunta ?? r.perguntaId);
      data.append(`resposta_${i + 1}_resposta`, r.textoOpcao);
      data.append(`resposta_${i + 1}_pontos`, String(r.pontos));
    });
    setFormEnviando(true);
    try {
      const nome = (form.querySelector('[name="nome"]') as HTMLInputElement)?.value || '';
      const email = (form.querySelector('[name="email"]') as HTMLInputElement)?.value || '';
      const whatsapp = (form.querySelector('[name="whatsapp"]') as HTMLInputElement)?.value || '';
      const empresa = (form.querySelector('[name="empresa"]') as HTMLInputElement)?.value || '';
      const segmento = (form.querySelector('[name="segmento"]') as HTMLSelectElement)?.value || '';
      const maiorDesafio = (form.querySelector('[name="maior_desafio"]') as HTMLTextAreaElement)?.value || '';
      const lgpdAceito = (form.querySelector('[name="lgpd_aceito"]') as HTMLInputElement)?.checked || false;
      const result = isQuizForm
        ? await saveLead({ nome, email, whatsapp, empresa, segmento, maior_desafio: maiorDesafio, diagnostico: {
            nivelNumero: nivelAtual.nivel,
            nivelTitulo: nivelAtual.titulo,
            pontuacaoTotal,
            pontuacaoMaxima,
            percentualGeral: Math.round((pontuacaoTotal / pontuacaoMaxima) * 100),
            areas: resultadosPorArea.map(ar => ({
              areaKey: ar.areaKey,
              area: ar.area,
              pontos: ar.pontos,
              maxPontos: ar.maxPontos,
              percentual: Math.round((ar.pontos / ar.maxPontos) * 100),
              diagnostico: ar.diagnostico,
            })),
          }, lgpd_aceito: lgpdAceito })
        : await saveLead({ nome, email, whatsapp: whatsapp.replace(/\D/g, ''), empresa, segmento, maior_desafio: maiorDesafio, lgpd_aceito: lgpdAceito });

      if (result.success) {
        setFormEnviado(true);
        setLeadEmail(email);
        setResultadoDesbloqueado(true);
      }
      else { setFormErro('Erro: ' + (result.error || 'desconhecido')); }
    } catch {
      setFormErro('Erro de conexao. Tente novamente.');
    } finally {
      setFormEnviando(false);
    }
  };

  const perguntaAtualId = sequencia[perguntaAtualIdx];
  const pergunta = perguntasMap[perguntaAtualId];
  const totalExibicao = sequencia.length;
  const progresso = (perguntaAtualIdx / totalExibicao) * 100;

  // Área jurídica separada para destaque
  const areaJuridica = resultadosPorArea.find((a) => a.areaKey === 'juridico');

  return (
    <section
      id="quiz"
      className={`${quizFullscreen ? 'fixed inset-0 z-[9999] flex flex-col' : 'py-24 md:py-32 relative overflow-hidden'} bg-gray-950`}
    >
      {/* Background decorations — only outside fullscreen */}
      {!quizFullscreen && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
        </div>
      )}

      {/* Botão fechar — só no fullscreen após iniciar */}
      {quizFullscreen && (
        <div className="flex-shrink-0 flex justify-end p-4">
          <button
            onClick={handleReiniciar}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-colors cursor-pointer"
            title="Fechar diagnóstico"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>
      )}

      {/* Botão voltar ao site — só no fullscreen quando ainda não terminou */}
      {quizFullscreen && (
        <div className="absolute top-4 left-4 z-10">
          <button
            onClick={handleReiniciar}
            className="flex items-center gap-2 text-white/40 hover:text-white text-sm transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            <span>Voltar ao site</span>
          </button>
        </div>
      )}

      <div
        className={`max-w-3xl mx-auto px-6 md:px-12 relative z-10 ${quizFullscreen ? 'flex-1 overflow-y-auto py-8 scrollbar-hide' : ''}`}
        style={quizFullscreen ? { scrollbarWidth: 'none', msOverflowStyle: 'none' } as any : undefined}
      >

        {/* ── TELA INICIAL ── */}
        {!iniciado && (
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-full px-4 py-2 mb-8">
              <i className="ri-timer-flash-line text-amber-400 text-sm" />
              <span className="text-amber-400 text-xs font-semibold tracking-widest uppercase">
                Diagnóstico Gratuito · 3 minutos
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-4">
              Qual o Status do<br />
              <span className="text-amber-400">Seu Negócio Hoje?</span>
            </h2>
            <p className="text-white/60 text-lg md:text-xl leading-relaxed mb-4 max-w-2xl mx-auto">
              Em 3 minutos descubra os gargalos que estão travando o crescimento do seu negócio — incluindo sua maturidade jurídica.
            </p>
            <p className="text-white/40 text-base mb-12 max-w-xl mx-auto">
              Diagnóstico completo por área com recomendações personalizadas.
            </p>
            <div className="flex flex-wrap justify-center gap-6 mb-12">
              {[
                { icon: 'ri-question-answer-line', label: 'Perguntas personalizadas' },
                { icon: 'ri-pie-chart-2-line', label: '8 áreas avaliadas' },
                { icon: 'ri-bar-chart-grouped-line', label: '4 níveis de resultado' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2 text-white/50">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5">
                    <i className={`${item.icon} text-amber-400 text-sm`} />
                  </div>
                  <span className="text-sm font-medium">{item.label}</span>
                </div>
              ))}
            </div>
            <div className="mb-12"><GaugeMeterDecorative /></div>
            <button
              onClick={handleIniciar}
              className="group inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-gray-950 font-black text-base md:text-lg px-6 md:px-10 py-4 md:py-5 rounded-2xl transition-all duration-300 hover:scale-105 cursor-pointer w-full sm:w-auto"
            >
              <i className="ri-play-circle-line text-xl md:text-2xl flex-shrink-0" />
              <span>Iniciar Diagnóstico Gratuito</span>
              <i className="ri-arrow-right-line text-lg md:text-xl flex-shrink-0 transition-transform group-hover:translate-x-1" />
            </button>
            <p className="text-white/30 text-sm mt-4">Sem cadastro · Resultado imediato · 100% gratuito</p>
          </div>
        )}

        {/* ── TELA DE PERGUNTAS ── */}
        {iniciado && !mostrarResultado && pergunta && (
          <div className={`transition-opacity duration-300 ${animando ? 'opacity-0' : 'opacity-100'}`}>
            <div className="mb-10">
              {/* Barra de progresso */}
              <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mb-3">
                <div className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full transition-all duration-500" style={{ width: `${((perguntaAtualIdx / totalExibicao) * 100)}%` }} />
              </div>
              {/* Contador simples */}
              <div className="flex items-center justify-between">
                <span className="text-white/50 text-xs">
                  <span className="text-amber-400 font-bold">{perguntaAtualIdx + 1}</span>/{totalExibicao} perguntas
                </span>
                <span className="text-white/40 text-xs">
                  <span className="text-amber-400 font-semibold">+{pontuacaoTotal} pts</span> conquistados
                </span>
              </div>
            </div>

            <div className="relative flex items-stretch gap-3">
              {/* Botão Voltar — lateral esquerda */}
              <div className="flex items-center">
                {perguntaAtualIdx > 0 ? (
                  <button
                    onClick={handleVoltar}
                    className="h-full min-h-[120px] w-10 flex flex-col items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/25 rounded-2xl transition-all cursor-pointer group"
                    title="Voltar"
                  >
                    <i className="ri-arrow-left-s-line text-white/40 group-hover:text-white text-xl transition-colors" />
                    <span className="text-white/30 group-hover:text-white/60 text-xs font-semibold transition-colors" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>Voltar</span>
                  </button>
                ) : (
                  <div className="w-10" />
                )}
              </div>

              {/* Card da pergunta */}
              <div className="flex-1 bg-white/5 border border-white/10 rounded-3xl p-6 md:p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-500/20">
                    <i className={`${pergunta.icon} text-amber-400 text-lg`} />
                  </div>
                  <span className="text-amber-400 text-sm font-semibold tracking-wider uppercase">{pergunta.area}</span>
                </div>
                <h3 className="text-xl md:text-2xl font-black text-white leading-tight mb-6">{pergunta.pergunta}</h3>
                <div className="space-y-3">
                  {pergunta.opcoes.map((opcao, index) => (
                    <button
                      key={index}
                      onClick={() => handleSelecionar(opcao.pontos, index)}
                      disabled={opcaoSelecionada !== null}
                      className={`w-full text-left flex items-center gap-4 p-4 rounded-2xl border transition-all duration-200 cursor-pointer whitespace-normal ${
                        opcaoSelecionada === index
                          ? 'bg-amber-500 border-amber-500 text-gray-950'
                          : opcaoSelecionada !== null
                          ? 'bg-white/3 border-white/5 text-white/30 cursor-not-allowed'
                          : 'bg-white/5 border-white/10 text-white hover:bg-white/10 hover:border-amber-500/40'
                      }`}
                    >
                      <div className={`w-8 h-8 flex items-center justify-center rounded-full border-2 flex-shrink-0 font-bold text-sm transition-all ${opcaoSelecionada === index ? 'border-gray-950 bg-gray-950 text-amber-400' : opcaoSelecionada !== null ? 'border-white/10 text-white/20' : 'border-white/20 text-white/50'}`}>
                        {String.fromCharCode(65 + index)}
                      </div>
                      <span className="font-medium text-sm md:text-base leading-snug">{opcao.texto}</span>
                      {opcaoSelecionada === index && <i className="ri-check-line ml-auto text-gray-950 text-xl flex-shrink-0" />}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <p className="text-center text-white/25 text-sm mt-5">Selecione a opção que melhor descreve sua realidade atual</p>
          </div>
        )}

        {/* ── TELA DE RESULTADO ── */}
        {mostrarResultado && (
          <div>
            {/* Header sem imagem */}
            <div className="rounded-2xl mb-6 p-6 bg-gradient-to-br from-amber-500/20 to-orange-500/10 border border-amber-500/20">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">{nivelAtual.emoji}</span>
                <span className="text-white/80 text-xs font-semibold tracking-widest uppercase">Seu Resultado</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-white leading-tight mb-1">{nivelAtual.titulo}</h2>
              <p className="text-white/60 text-base">{nivelAtual.subtitulo}</p>
            </div>

            {/* Gauge + pontuação */}
            <div className="flex flex-col md:flex-row items-center gap-6 mb-6">
              <div className="flex-1 flex flex-col items-start">
                <GaugeMeter posicao={nivelAtual.posicao} size={240} />
              </div>
              <div className="flex flex-row md:flex-col gap-4">
                <div className="bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-center">
                  <p className="text-white/50 text-xs uppercase tracking-widest mb-1">Pontos</p>
                  <p className="text-4xl font-black text-amber-400 leading-none">
                    {pontuacaoTotal}<span className="text-white/30 text-xl font-normal"> pts</span>
                  </p>
                  <p className="text-white/30 text-xs mt-1">de {pontuacaoMaxima} pts possíveis</p>
                </div>
                <div className={`${nivelAtual.bgCard} border ${nivelAtual.borderColor} rounded-2xl px-8 py-5 text-center`}>
                  <p className="text-gray-500 text-xs uppercase tracking-widest mb-1">Nível</p>
                  <p className={`text-4xl font-black ${nivelAtual.textColor} leading-none`}>
                    {nivelAtual.nivel}<span className="text-gray-400 text-xl font-normal">/4</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Blur overlay com preview das áreas + CTA de desbloqueio no topo */}
            {!resultadoDesbloqueado && (
              <div className="relative mb-6">
                {/* Preview das áreas — tudo embaçado */}
                <div className="filter blur-sm pointer-events-none select-none opacity-60 mb-[-2rem]">
                  <div className="flex gap-2 mb-4 bg-white/5 border border-white/10 p-1 rounded-full">
                    <div className="flex-1 py-2.5 rounded-full text-sm font-bold bg-amber-500/50 text-gray-950 text-center">Diagnóstico Geral</div>
                    <div className="flex-1 py-2.5 rounded-full text-sm font-bold text-white/60 text-center">Resultado por Área</div>
                  </div>
                  <div className="space-y-3">
                    {resultadosPorArea.map((ar) => {
                      const pct = Math.round((ar.pontos / ar.maxPontos) * 100);
                      return (
                        <div key={ar.areaKey} className={`${ar.corBg} border border-white/10 rounded-2xl p-4 opacity-50`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${ar.cor}`}>
                                <i className={`${ar.icon} text-white text-xs`} />
                              </div>
                              <span className={`font-black text-xs ${ar.corText}`}>{ar.area}</span>
                            </div>
                            <span className={`text-xs font-bold ${ar.corText}`}>{pct}%</span>
                          </div>
                          <BarraArea pontos={ar.pontos} maxPontos={ar.maxPontos} cor={ar.cor} />
                        </div>
                      );
                    })}
                  </div>
                  {/* Empurre o conteúdo para parecer que o modal flutua sobre as áreas */}
                  <div className="h-32" />
                </div>

                {/* Modal de desbloqueio — flutua no topo das áreas embaçadas */}
                <div className="absolute inset-x-0 top-0 z-10 px-4">
                  <div className="bg-gray-900/95 backdrop-blur-sm border border-amber-500/40 rounded-2xl p-5 text-center shadow-xl shadow-amber-500/10">
                    <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-amber-500/20 mx-auto mb-3">
                      <i className="ri-lock-unlock-line text-amber-400 text-xl" />
                    </div>
                    <h3 className="text-white font-black text-lg mb-1">Desbloqueie o Diagnóstico Completo</h3>
                    <p className="text-white/50 text-xs mb-4 leading-relaxed">
                      Preencha seus dados para ver a análise detalhada por área.
                    </p>
                    <form ref={formRef} id="form-quiz-resultado" data-readdy-form onSubmit={handleFormSubmit} className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <input type="text" name="nome" required placeholder="Seu nome" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/25 focus:outline-none focus:border-amber-500/50 transition-colors" />
                        </div>
                        <div>
                          <input type="tel" name="whatsapp" required placeholder="WhatsApp" pattern="[0-9]{10,11}" maxLength={11} onInput={(e) => { (e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.replace(/\D/g, ''); }} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/25 focus:outline-none focus:border-amber-500/50 transition-colors" />
                        </div>
                      </div>
                      <div>
                        <input type="text" name="empresa" required placeholder="Nome da empresa *" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/25 focus:outline-none focus:border-amber-500/50 transition-colors" />
                      </div>
                      <div>
                        <select name="segmento" required className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-amber-500/50 transition-colors [&>option]:text-gray-900">
                          <option value="">Segmento da empresa</option>
                          <option value="servicos">Serviços</option>
                          <option value="comercio_varejo">Comércio / Varejo</option>
                          <option value="industria">Indústria</option>
                          <option value="tecnologia">Tecnologia</option>
                          <option value="construcao">Construção Civil</option>
                          <option value="alimentacao">Alimentação</option>
                          <option value="saude">Saúde</option>
                          <option value="educacao">Educação</option>
                          <option value="outro">Outro</option>
                        </select>
                      </div>
                      <div className="flex items-start gap-2 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5">
                        <input type="checkbox" name="lgpd_aceito" id="lgpd_aceito" value="1" required className="mt-0.5 accent-amber-500 cursor-pointer flex-shrink-0" />
                        <label htmlFor="lgpd_aceito" className="text-white/40 text-xs leading-relaxed cursor-pointer text-left">
                          Li e aceito a <a href="/politica-de-privacidade" target="_blank" rel="noopener noreferrer" className="text-amber-400 underline">Política de Privacidade</a> e autorizo contato.
                        </label>
                      </div>
                      {formErro && <p className="text-red-400 text-xs text-center">{formErro}</p>}
                      <button type="submit" disabled={formEnviando} className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-gray-950 font-black text-sm py-3.5 rounded-xl transition-all duration-300 cursor-pointer">
                        {formEnviando ? (
                          <><i className="ri-loader-4-line animate-spin text-base" /><span>Enviando...</span></>
                        ) : (
                          <><i className="ri-shield-unlock-line text-base" /><span>Desbloquear Diagnóstico</span></>
                        )}
                      </button>
                      <p className="text-white/25 text-xs text-center">Dados confidenciais · Sem spam</p>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {/* Resultado desbloqueado — áreas detalhadas */}
            {resultadoDesbloqueado && (
              <>
                {/* Abas */}
                <div className="flex gap-2 mb-6 bg-white/5 border border-white/10 p-1 rounded-full">
                  <button
                    onClick={() => setAbaResultado('geral')}
                    className={`flex-1 py-2.5 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${abaResultado === 'geral' ? 'bg-amber-500 text-gray-950' : 'text-white/60 hover:text-white border border-transparent hover:border-white/20'}`}
                  >
                    Diagnóstico Geral
                  </button>
                  <button
                    onClick={() => setAbaResultado('areas')}
                    className={`flex-1 py-2.5 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${abaResultado === 'areas' ? 'bg-amber-500 text-gray-950' : 'text-white/60 hover:text-white border border-transparent hover:border-white/20'}`}
                  >
                    Resultado por Área
                  </button>
                </div>

                {/* Aba Geral */}
                {abaResultado === 'geral' && (
                  <div className={`${nivelAtual.bgCard} border-2 ${nivelAtual.borderColor} rounded-2xl p-6 mb-6`}>
                    <p className={`${nivelAtual.textColor} font-black text-xl mb-5`}>
                      &ldquo;{nivelAtual.mensagem}&rdquo;
                    </p>
                    <p className="text-gray-600 text-sm font-semibold uppercase tracking-wider mb-4">
                      Empresas no seu nível normalmente enfrentam:
                    </p>
                    <ul className="space-y-3">
                      {nivelAtual.problemas.map((prob) => (
                        <li key={prob} className="flex items-start gap-3 text-gray-700 text-sm">
                          <div className={`w-5 h-5 flex items-center justify-center rounded-full ${nivelAtual.badgeBg} flex-shrink-0 mt-0.5`}>
                            <i className={`ri-error-warning-line ${nivelAtual.textColor} text-xs`} />
                          </div>
                          {prob}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Aba Por Área */}
                {abaResultado === 'areas' && (
                  <div className="space-y-4 mb-6">
                    {resultadosPorArea.map((ar) => {
                      const pct = Math.round((ar.pontos / ar.maxPontos) * 100);
                      return (
                        <div key={ar.areaKey} className={`${ar.corBg} border border-white/10 rounded-2xl p-5`}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${ar.cor}`}>
                                <i className={`${ar.icon} text-white text-sm`} />
                              </div>
                              <span className={`font-black text-sm ${ar.corText}`}>{ar.area}</span>
                            </div>
                            <span className={`text-xs font-bold ${ar.corText}`}>{pct}%</span>
                          </div>
                          <BarraArea pontos={ar.pontos} maxPontos={ar.maxPontos} cor={ar.cor} />
                          <p className="text-gray-700 text-xs mt-3 font-semibold">{ar.diagnostico}</p>
                          <p className="text-gray-500 text-xs mt-1 leading-relaxed">
                            <strong className="text-gray-600">Recomendação:</strong> {ar.recomendacao}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}


              </>
            )}

            {/* CTA */}
            {!programaInteresse ? (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-3xl p-8 mb-8 text-center">
                <p className="text-amber-400 text-xs font-semibold uppercase tracking-widest mb-3">Próximo passo recomendado</p>
                <h3 className="text-2xl md:text-3xl font-black text-white mb-3 leading-tight">
                  Transforme seu negócio em<br />
                  <span className="text-amber-400">60 dias com mentoria presencial</span>
                </h3>
                <p className="text-white/50 text-base mb-8 max-w-lg mx-auto">
                  O programa <strong className="text-white">Transformação Real 60D</strong> foi criado exatamente para empresários no seu nível — com método prático, presencial e resultado garantido.
                </p>
                {!programaInteresse ? (
                  <button
                    onClick={async () => {
                      if (leadEmail) {
                        await markAsInteressado(leadEmail);
                      }
                      setProgramaInteresse(true);
                    }}
                    className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-gray-950 font-black text-sm md:text-base px-6 md:px-8 py-4 rounded-xl transition-all duration-300 hover:scale-105 cursor-pointer w-full sm:w-auto text-center"
                  >
                    <i className="ri-rocket-line text-lg flex-shrink-0" />
                    <span>Quero Participar do Transformação Real 60D</span>
                  </button>
                ) : null}
                <br />
                <a href="#proximos-passos" className="inline-flex items-center justify-center gap-2 text-white/40 hover:text-white/70 text-sm mt-4 transition-colors cursor-pointer text-center flex-wrap">
                  <i className="ri-calendar-line text-sm flex-shrink-0" />
                  <span>Ou agendar um diagnóstico presencial primeiro</span>
                </a>
              </div>
            ) : (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-3xl p-8 mb-8 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-amber-500/20 mx-auto mb-4">
                  <i className="ri-loader-4-line text-amber-400 text-3xl" />
                </div>
                <h3 className="text-white font-black text-xl mb-2">Solicitação Enviada!</h3>
                <p className="text-white/50 text-sm max-w-sm mx-auto">
                  Nossa equipe entrará em contato em breve para passar as instruções do programa Transformação Real 60D.
                </p>
              </div>
            )}

            <div className="text-center">
              <button onClick={handleReiniciar} className="inline-flex items-center gap-2 text-white/30 hover:text-white/60 text-sm transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-refresh-line" />
                Refazer o diagnóstico
              </button>
            </div>
          </div>
        )}
      </div>

    </section>
  );
}
